import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { BannerContext } from '../../App';
import {
  FACE_CAPTURE,
  FACE_DETECTION,
  isUiPreviewEnabled,
} from '../../config';
import {
  captureFaceImage,
  prepareFeatureExtractionRequest,
  requestFaceCamera,
  stopMediaStream,
} from '../../utils/faceCapture';
import { getVerificationStatus, requestFaceVerification } from '../../utils';
import {
  createFaceDetector,
  evaluateFaceFrame,
} from '../../utils/faceDetector';
import ErrorFallback from '../Error/ErrorFallback';
import './FaceCapturePage.css';

const FACE_ERROR_MESSAGES = {
  camera_not_supported: '이 브라우저에서는 카메라를 사용할 수 없습니다.',
  camera_not_ready: '카메라 준비가 끝나지 않았습니다.',
  camera_canvas_unavailable: '촬영 화면을 처리할 수 없습니다.',
  face_image_encoding_failed: '촬영한 사진을 처리하지 못했습니다.',
  face_detector_load_failed: '얼굴 인식 기능을 준비하지 못했습니다.',
  face_detector_runtime_failed: '얼굴을 인식하는 중 오류가 발생했습니다.',
  verification_request_id_missing: '확인할 티켓 인증 요청이 없습니다.',
  verification_request_id_mismatch: '티켓 인증 요청이 일치하지 않습니다.',
  verification_status_unavailable: '티켓 인증 상태를 확인할 수 없습니다.',
  invalid_verification_status_response: '티켓 인증 상태 응답이 올바르지 않습니다.',
  verification_not_completed: 'Wallet 인증이 완료되지 않았습니다.',
  face_image_missing: '촬영한 얼굴 이미지를 확인할 수 없습니다.',
  face_verification_timeout: '얼굴 인증 시간이 초과되었습니다. 다시 시도해 주세요.',
  face_verification_unavailable: '얼굴 인증 서버에 연결할 수 없습니다.',
  invalid_face_verification_response: '얼굴 인증 서버의 응답이 올바르지 않습니다.',
};

const FACE_RETRY_MESSAGES = {
  face_mismatch: '얼굴이 등록된 사용자와 일치하지 않습니다. 다시 시도해 주세요.',
  no_face: '얼굴을 확인하지 못했습니다. 정면을 바라보고 다시 시도해 주세요.',
  multiple_faces: '여러 얼굴이 감지되었습니다. 한 명만 화면에 나오도록 해 주세요.',
};

const FACE_DETECTION_MESSAGES = {
  loading: '얼굴 인식 기능을 준비하고 있습니다.',
  'no-face': '얼굴을 가이드 안에 맞춰 주세요.',
  'multiple-faces': '한 명만 화면에 나오도록 해 주세요.',
  'too-far': '카메라에 조금 더 가까이 와 주세요.',
  'off-center': '얼굴을 가이드 중앙에 맞춰 주세요.',
  ready: '얼굴을 인식했습니다. 잠시 그대로 있어 주세요.',
};

const ROLE_LABELS = {
  PRIMARY: '대표 구매자',
  REPRESENTATIVE: '대표 구매자',
  COMPANION: '동반 구매자',
};

const BANNER_TEXT = {
  checking: '티켓 인증 상태를 확인하고 있습니다.',
  camera: '얼굴을 가이드에 맞추고 정면을 바라봐 주세요.',
  processing: '얼굴을 확인하고 있습니다.',
  retry: '얼굴 인증에 실패했습니다.',
  success: '얼굴 인증이 완료되었습니다.',
  error: '얼굴 인증을 진행할 수 없습니다.',
};

const toFaceErrorMessage = (error) => {
  if (error?.name === 'NotAllowedError') {
    return '카메라 권한이 필요합니다. 브라우저 설정에서 카메라를 허용해 주세요.';
  }
  if (error?.name === 'NotFoundError') {
    return '사용할 수 있는 카메라를 찾지 못했습니다.';
  }
  if (String(error?.message).startsWith('verification_status_http_')) {
    return '티켓 인증 상태를 확인하지 못했습니다.';
  }
  if (error?.httpStatus === 404) {
    return '티켓 인증 요청이 만료되었습니다. QR 인증부터 다시 진행해 주세요.';
  }
  if (error?.httpStatus === 409) {
    return '티켓 인증이 완료되지 않았습니다. QR 인증부터 다시 진행해 주세요.';
  }
  if (String(error?.message).startsWith('face_verification_http_')) {
    return '얼굴 인증을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.';
  }
  return FACE_ERROR_MESSAGES[error?.message] || '얼굴 촬영을 시작하지 못했습니다.';
};

const toFaceRetryMessage = (reason) =>
  FACE_RETRY_MESSAGES[reason] ||
  '얼굴 인증에 실패했습니다. 얼굴이 정면으로 보이도록 다시 시도해 주세요.';

function StatusIcon({ type }) {
  return (
    <div
      className={`face-status-icon face-status-icon--${type}`}
      aria-hidden='true'
    />
  );
}

StatusIcon.propTypes = {
  type: PropTypes.oneOf(['success']).isRequired,
};

export default function FaceCapturePage({ previewState }) {
  const setBannerText = useContext(BannerContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('requestId') || '';
  const manualMode =
    searchParams.get('mode') === 'manual' && isUiPreviewEnabled();
  const isPreview = Boolean(previewState);
  const [screen, setScreen] = useState(
    previewState || (manualMode ? 'camera' : 'checking')
  );
  const [sessionReady, setSessionReady] = useState(isPreview || manualMode);
  const [role, setRole] = useState(
    location.state?.role || searchParams.get('role') || ''
  );
  const [cameraReady, setCameraReady] = useState(false);
  const [detectorReady, setDetectorReady] = useState(false);
  const [detectorAttempt, setDetectorAttempt] = useState(0);
  const [detectionState, setDetectionState] = useState(
    isPreview ? 'ready' : 'loading'
  );
  const [detectionProgress, setDetectionProgress] = useState(
    isPreview ? 0.7 : 0
  );
  const [errorMessage, setErrorMessage] = useState(
    previewState === 'retry'
      ? '얼굴 인증에 실패했습니다. 얼굴이 정면으로 보이도록 다시 시도해 주세요.'
      : previewState === 'error'
        ? '카메라 권한이 필요합니다. 브라우저 설정에서 카메라를 허용해 주세요.'
        : ''
  );
  const [errorKind, setErrorKind] = useState(
    previewState === 'retry'
      ? 'face'
      : previewState === 'error'
        ? 'camera'
        : ''
  );
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const featureRequestRef = useRef(null);
  const faceVerificationControllerRef = useRef(null);
  const mountedRef = useRef(true);
  const autoCaptureStartedRef = useRef(false);

  const showsCamera =
    ['camera', 'processing', 'success', 'retry'].includes(screen) ||
    (screen === 'error' && ['capture', 'detector', 'face'].includes(errorKind));
  const shouldKeepCamera = showsCamera && (isPreview || sessionReady);

  const clearCamera = useCallback(() => {
    stopMediaStream(streamRef.current);
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraReady(false);
  }, []);

  const clearFeatureRequest = useCallback(() => {
    featureRequestRef.current = null;
  }, []);

  const abortFaceVerification = useCallback(() => {
    faceVerificationControllerRef.current?.abort();
    faceVerificationControllerRef.current = null;
  }, []);

  const takePhoto = useCallback(async () => {
    if (
      isPreview ||
      autoCaptureStartedRef.current ||
      !cameraReady ||
      !videoRef.current
    )
      return;

    autoCaptureStartedRef.current = true;

    if (manualMode) {
      setDetectionProgress(1);
      return;
    }

    let preparedRequest = null;
    let verificationController = null;
    let verificationStarted = false;

    try {
      const pendingCapture = captureFaceImage(videoRef.current, {
        quality: FACE_CAPTURE.JPEG_QUALITY,
        maxWidth: FACE_CAPTURE.MAX_WIDTH,
      });
      setScreen('processing');
      const faceImage = await pendingCapture;
      if (!mountedRef.current) return;

      preparedRequest = prepareFeatureExtractionRequest({
        requestId,
        role,
        faceImage,
      });
      featureRequestRef.current = preparedRequest;

      abortFaceVerification();
      verificationController = new AbortController();
      faceVerificationControllerRef.current = verificationController;
      verificationStarted = true;

      const result = await requestFaceVerification({
        requestId,
        imageFile: preparedRequest.imageFile,
        signal: verificationController.signal,
      });
      if (!mountedRef.current || verificationController.signal.aborted) return;

      setErrorKind('');
      if (result.isValid) {
        setErrorMessage('');
        setScreen('success');
        return;
      }

      setErrorKind('face');
      setErrorMessage(toFaceRetryMessage(result.reason));
      setScreen('retry');
    } catch (error) {
      if (!mountedRef.current || error?.name === 'AbortError') return;
      autoCaptureStartedRef.current = false;
      const isInvalidSession =
        verificationStarted && [404, 409].includes(error?.httpStatus);
      setErrorKind(
        isInvalidSession ? 'session' : verificationStarted ? 'face' : 'capture'
      );
      setErrorMessage(toFaceErrorMessage(error));
      setScreen('error');
    } finally {
      if (faceVerificationControllerRef.current === verificationController) {
        faceVerificationControllerRef.current = null;
      }
      if (featureRequestRef.current === preparedRequest) {
        clearFeatureRequest();
      }
    }
  }, [
    abortFaceVerification,
    cameraReady,
    clearFeatureRequest,
    isPreview,
    manualMode,
    requestId,
    role,
  ]);

  useEffect(() => {
    if (screen === 'error' && errorKind === 'session') {
      setBannerText('티켓 인증 상태를 확인할 수 없습니다.');
      return;
    }
    setBannerText(BANNER_TEXT[screen] || BANNER_TEXT.camera);
  }, [errorKind, screen, setBannerText]);

  useEffect(() => {
    if (isPreview || !sessionReady) return undefined;

    let active = true;
    let createdDetector = null;
    setDetectorReady(false);
    setDetectionState('loading');

    const loadDetector = async () => {
      try {
        createdDetector = await createFaceDetector();
        if (!active) {
          createdDetector.close();
          return;
        }

        detectorRef.current = createdDetector;
        setDetectorReady(true);
        setDetectionState('no-face');
      } catch {
        if (!active) return;
        setErrorKind('detector');
        setErrorMessage(toFaceErrorMessage(new Error('face_detector_load_failed')));
        setScreen('error');
      }
    };

    loadDetector();
    return () => {
      active = false;
      if (detectorRef.current === createdDetector) {
        detectorRef.current = null;
      }
      createdDetector?.close();
    };
  }, [detectorAttempt, isPreview, sessionReady]);

  useEffect(() => {
    if (isPreview || manualMode) return undefined;

    const controller = new AbortController();
    let active = true;
    setSessionReady(false);
    setRole('');
    setErrorKind('');
    setErrorMessage('');
    setScreen('checking');
    clearCamera();

    const validateSession = async () => {
      try {
        const data = await getVerificationStatus({
          requestId,
          signal: controller.signal,
        });
        if (!active) return;
        if (data.status !== 'verified') {
          throw new Error('verification_not_completed');
        }

        setSessionReady(true);
        setRole(data.role || location.state?.role || '');
        setScreen('camera');
      } catch (error) {
        if (!active || error?.name === 'AbortError') return;
        setErrorKind('session');
        setErrorMessage(toFaceErrorMessage(error));
        setScreen('error');
      }
    };

    validateSession();
    return () => {
      active = false;
      controller.abort();
    };
  }, [clearCamera, isPreview, location.state?.role, manualMode, requestId]);

  useEffect(() => {
    if (!shouldKeepCamera) return undefined;

    let active = true;
    setCameraReady(false);

    const startCamera = async () => {
      try {
        const stream = await requestFaceCamera();
        if (!active) {
          stopMediaStream(stream);
          return;
        }

        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) {
          throw new Error('camera_not_ready');
        }
        video.srcObject = stream;
        await video.play();
        if (active) setCameraReady(true);
      } catch (error) {
        if (!active) return;
        clearCamera();
        setErrorKind('camera');
        setErrorMessage(toFaceErrorMessage(error));
        setScreen('error');
      }
    };

    startCamera();
    return () => {
      active = false;
      clearCamera();
    };
  }, [clearCamera, shouldKeepCamera]);

  useEffect(() => {
    if (screen !== 'camera') return;
    autoCaptureStartedRef.current = false;
    setDetectionProgress(isPreview ? 0.7 : 0);
    setDetectionState(
      isPreview ? 'ready' : detectorRef.current ? 'no-face' : 'loading'
    );
  }, [isPreview, screen]);

  useEffect(() => {
    if (
      screen !== 'camera' ||
      isPreview ||
      !cameraReady ||
      !detectorReady ||
      !detectorRef.current
    )
      return undefined;

    let active = true;
    let animationFrameId = null;
    let lastScanAt = 0;
    let lastVideoTime = -1;
    let stableSince = null;
    let lastReadyAt = null;
    let stableFrameCount = 0;
    const maxStableGapMs = Math.max(
      500,
      FACE_DETECTION.SCAN_INTERVAL_MS * 3
    );

    const scanFrame = (timestamp) => {
      if (!active || autoCaptureStartedRef.current) return;

      const video = videoRef.current;
      const shouldScan =
        video?.readyState >= 2 &&
        video.currentTime !== lastVideoTime &&
        timestamp - lastScanAt >= FACE_DETECTION.SCAN_INTERVAL_MS;

      if (shouldScan) {
        lastScanAt = timestamp;
        lastVideoTime = video.currentTime;

        try {
          const result = detectorRef.current.detectForVideo(video, timestamp);
          const frame = evaluateFaceFrame({
            detections: result.detections,
            videoWidth: video.videoWidth,
            videoHeight: video.videoHeight,
          });

          if (frame.state === 'ready') {
            const readyGap =
              lastReadyAt === null ? 0 : timestamp - lastReadyAt;
            if (stableSince === null || readyGap > maxStableGapMs) {
              stableSince = timestamp;
              stableFrameCount = 1;
            } else {
              stableFrameCount += 1;
            }
            lastReadyAt = timestamp;
            const progress = Math.min(
              1,
              (timestamp - stableSince) / FACE_DETECTION.STABLE_DURATION_MS
            );
            setDetectionState('ready');
            setDetectionProgress(progress);

            if (progress >= 1 && stableFrameCount >= 4) {
              void takePhoto();
              return;
            }
          } else {
            stableSince = null;
            lastReadyAt = null;
            stableFrameCount = 0;
            setDetectionProgress(0);
            setDetectionState(frame.state);
          }
        } catch {
          setDetectorReady(false);
          setErrorKind('detector');
          setErrorMessage(
            toFaceErrorMessage(new Error('face_detector_runtime_failed'))
          );
          setScreen('error');
          return;
        }
      }

      animationFrameId = requestAnimationFrame(scanFrame);
    };

    animationFrameId = requestAnimationFrame(scanFrame);
    return () => {
      active = false;
      if (animationFrameId !== null) cancelAnimationFrame(animationFrameId);
    };
  }, [cameraReady, detectorReady, isPreview, screen, takePhoto]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopMediaStream(streamRef.current);
      detectorRef.current?.close();
      detectorRef.current = null;
      faceVerificationControllerRef.current?.abort();
      faceVerificationControllerRef.current = null;
      featureRequestRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (screen !== 'success' || isPreview) return undefined;
    const timer = setTimeout(() => navigate('/', { replace: true }), 3000);
    return () => clearTimeout(timer);
  }, [isPreview, navigate, screen]);

  const returnToQr = () => {
    abortFaceVerification();
    clearCamera();
    clearFeatureRequest();
    navigate('/', { replace: true });
  };

  const retryCapture = () => {
    if (!sessionReady) {
      returnToQr();
      return;
    }
    abortFaceVerification();
    clearFeatureRequest();
    setErrorKind('');
    setErrorMessage('');
    autoCaptureStartedRef.current = false;
    setDetectionProgress(0);
    setDetectionState(detectorReady ? 'no-face' : 'loading');
    if (!detectorReady) setDetectorAttempt((attempt) => attempt + 1);
    setScreen('camera');
  };

  const roleLabel = ROLE_LABELS[String(role).toUpperCase()] || '';
  const hasResultOverlay = screen === 'processing' || screen === 'success';
  const hasErrorModal = screen === 'retry' || screen === 'error';
  const displayedErrorMessage =
    errorMessage ||
    (screen === 'retry'
      ? '얼굴 인증에 실패했습니다. 얼굴이 정면으로 보이도록 다시 시도해 주세요.'
      : '얼굴 촬영을 시작하지 못했습니다.');

  if (screen === 'checking') {
    return (
      <main className='face-stage'>
        <section className='face-status-card' aria-live='polite'>
          <div className='face-spinner' />
          <h1>티켓 인증 확인 중</h1>
          <p>얼굴 촬영을 시작하기 전에 Wallet 인증 결과를 확인하고 있습니다.</p>
        </section>
      </main>
    );
  }

  return (
    <>
      <main className='face-stage'>
        <section className='face-camera-card' aria-live='polite'>
          <div
            className={`face-camera-content ${
              hasResultOverlay || hasErrorModal ? 'is-blurred' : ''
            }`}
          >
            <div className='face-camera-frame'>
              <video
                ref={videoRef}
                className='face-camera-video'
                autoPlay
                muted
                playsInline
                onCanPlay={() => setCameraReady(true)}
              />
              {screen === 'camera' && (
                <div
                  className={`face-guide ${
                    detectionState === 'ready' ? 'is-detected' : ''
                  }`}
                  aria-hidden='true'
                />
              )}
              {!cameraReady && (
                <div className='face-camera-loading'>
                  {errorKind === 'camera'
                    ? '카메라를 불러올 수 없습니다.'
                    : '카메라 준비 중...'}
                </div>
              )}
            </div>

            <div className='face-detection-panel' aria-live='polite'>
              <p className='face-camera-guide-text'>
                {screen === 'camera'
                  ? FACE_DETECTION_MESSAGES[detectionState] ||
                    FACE_DETECTION_MESSAGES['no-face']
                  : screen === 'success'
                    ? '얼굴 인증이 완료되었습니다.'
                    : '촬영한 얼굴 정보를 확인하고 있습니다.'}
              </p>
              <div className='face-detection-progress' aria-hidden='true'>
                <span
                  style={{
                    width: `${
                      screen === 'camera'
                        ? Math.round(detectionProgress * 100)
                        : 100
                    }%`,
                  }}
                />
              </div>
              <small>
                {screen === 'camera'
                  ? '얼굴이 인식되면 자동으로 촬영됩니다.'
                  : '잠시만 기다려 주세요.'}
              </small>
            </div>
          </div>

          {screen === 'processing' && (
            <div className='face-camera-overlay'>
              <div className='face-spinner' aria-label='얼굴 인증 중' />
            </div>
          )}

          {screen === 'success' && (
            <div className='face-camera-overlay fade-in'>
              <StatusIcon type='success' />
              <strong className='face-overlay-label'>입장 가능합니다</strong>
              {roleLabel && (
                <span className='face-overlay-detail'>
                  {roleLabel} 얼굴 인증 완료
                </span>
              )}
            </div>
          )}

          {manualMode && screen === 'camera' && (
            <button type='button' className='face-link-button' onClick={returnToQr}>
              QR 화면으로 돌아가기
            </button>
          )}
        </section>
      </main>
      {hasErrorModal && (
        <ErrorFallback
          message={displayedErrorMessage}
          onRetry={errorKind === 'session' ? returnToQr : retryCapture}
          secondaryAction={
            errorKind === 'session'
              ? undefined
              : {
                  label: 'QR 화면으로 돌아가기',
                  onClick: returnToQr,
                }
          }
        />
      )}
    </>
  );
}

FaceCapturePage.propTypes = {
  previewState: PropTypes.oneOf([
    'camera',
    'processing',
    'retry',
    'success',
    'error',
  ]),
};
